import {type ReactNode, useState} from 'react';
import {ArrowLeftRight, ChevronRight, Copy, Eye, EyeOff, KeyRound} from 'lucide-react';
import {PhoneHQButton} from 'ch3-ui-lib';
import {useAppDispatch} from '../../../store/store';
import {showToast} from '../../../store/features/Notifications/notificationsSlice';
import {useGenerateSecretKeyMutation} from '../../../store/features/integrations/integrationsService';
import {SecretText} from './webhookUi';

const SECRET_BOX = 'flex h-[34px] w-full min-w-0 items-center rounded-md border border-line bg-zinc-50 px-3 font-mono text-xs tracking-[0.02em] text-ink-secondary sm:w-[340px]';

const Code = ({children}: {readonly children: ReactNode}) => <code className="font-mono text-xs">{children}</code>;

const VERIFY_STEPS: readonly (readonly [string, ReactNode])[] = [
    ['1. Check the signature', <>Verify <Code>webhook-id</Code>, <Code>webhook-timestamp</Code> and <Code>webhook-signature</Code> with any Standard Webhooks library.</>],
    ['2. Use this secret', <>Pass it as the key, with or without the <Code>whsec_</Code> prefix. The legacy <Code>X-Signature</Code> header (Base64 HMAC-SHA256 of the body) is still sent.</>],
    ['3. Ignore duplicates', <><Code>webhook-id</Code> and <Code>X-Idempotency-Key</Code> stay the same across retries.</>],
];

export function SigningSecretCard({secret}: {readonly secret: string | undefined}) {
    const dispatch = useAppDispatch();
    const [shown, setShown] = useState(false);
    const [confirmRotate, setConfirmRotate] = useState(false);
    const [verifyOpen, setVerifyOpen] = useState(false);
    const [generateSecretKey, {isLoading: rotating}] = useGenerateSecretKeyMutation();
    const notify = (description: string, type: 'success' | 'error' = 'success') => dispatch(showToast({description, type}));

    const copy = () => navigator.clipboard.writeText(secret ?? '')
        .then(() => notify('Signing secret copied'))
        .catch(() => notify('Signing secret could not be copied', 'error'));

    const rotate = async () => {
        try {
            await generateSecretKey().unwrap();
            setConfirmRotate(false);
            setShown(false);
            notify('Signing secret rotated');
        } catch {
            notify('Signing secret could not be rotated', 'error');
        }
    };

    return <section aria-label="Signing secret" className="rounded-[10px] border border-line bg-surface shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <div className="flex flex-wrap items-center gap-4 px-5 py-4">
            <div className="flex min-w-[260px] flex-1 items-start gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-ink-secondary"><KeyRound size={16} className="!p-0"/></span>
                <div>
                    <div className="font-semibold text-ink">Signing secret</div>
                    <p className="mt-0.5 text-[13px] text-ink-muted">Shared by all webhooks. Use it to verify that requests come from PhoneHQ.</p>
                </div>
            </div>
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
                {shown && secret
                    ? <div className={SECRET_BOX}><SecretText label="Signing secret" value={secret}/></div>
                    : <div className={SECRET_BOX} aria-label="Signing secret hidden">{'•'.repeat(32)}</div>}
                <PhoneHQButton phq-id="webhookList-showKeyBtn" variant="secondary" size="sm" disabled={!secret} onClick={() => setShown(!shown)}>
                    {shown ? <EyeOff size={14} className="!p-0"/> : <Eye size={14} className="!p-0"/>}{shown ? 'Hide' : 'Show'}
                </PhoneHQButton>
                <PhoneHQButton phq-id="webhookList-copyKeyBtn" variant="secondary" size="sm" disabled={!secret} onClick={() => void copy()}>
                    <Copy size={14} className="!p-0"/>Copy
                </PhoneHQButton>
                <PhoneHQButton phq-id="webhookList-rotateNewKeyBtn" variant="secondary" size="sm" disabled={!secret} className="!text-danger-ink hover:!bg-danger-tint" onClick={() => setConfirmRotate(true)}>
                    <ArrowLeftRight size={14} className="!p-0 !text-danger-ink"/>Rotate
                </PhoneHQButton>
            </div>
        </div>
        {confirmRotate && <div role="alert" className="mx-5 mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-danger-tint bg-danger-tint px-3.5 py-3">
            <p className="min-w-[240px] flex-1 text-[13px] text-danger-ink">Your endpoints will reject requests until they use the new secret. Rotate now?</p>
            <PhoneHQButton variant="secondary" size="sm" onClick={() => setConfirmRotate(false)}>Cancel</PhoneHQButton>
            <PhoneHQButton phq-id="webhookList-rotateConfirmBtn" variant="primary" size="sm" disabled={rotating} style={{background: '#dc2626', borderColor: '#dc2626'}} onClick={() => void rotate()}>
                {rotating ? 'Rotating…' : 'Rotate secret'}
            </PhoneHQButton>
        </div>}
        <div className="border-t border-line px-5 py-2.5">
            <button type="button" aria-expanded={verifyOpen} onClick={() => setVerifyOpen(!verifyOpen)}
                className="inline-flex items-center gap-1.5 py-1 text-[13px] font-medium text-indigo-500">
                <ChevronRight size={14} className={`!p-0 !text-indigo-500 transition-transform ${verifyOpen ? 'rotate-90' : ''}`}/>
                How to verify signatures
            </button>
            {verifyOpen && <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-3 pb-2 pt-2.5">
                {VERIFY_STEPS.map(([title, body]) => <div key={title} className="rounded-lg border border-line bg-zinc-50 p-3.5 text-[13px] leading-[1.55] text-ink-secondary">
                    <div className="mb-1.5 font-semibold text-ink">{title}</div>
                    {body}
                </div>)}
            </div>}
        </div>
    </section>;
}
